import { useEffect, useRef, useState } from "react";
import { Loader, Textarea } from "@mantine/core";
import { IconAlertTriangle, IconSend } from "@tabler/icons-react";
import {
  ANSWER_SIDES,
  AnswerCardContent,
  AnswerChip,
  AnswerSide,
} from "./AnswerCard";

/** One line for an answer: the product's name and size, or what the Skip meant. */
const summarizeAnswerCardContent = (content: AnswerCardContent): string => {
  if (content.kind === "skip") {
    return content.description;
  }

  const { cluster, variant, title } = content.product;
  if (!cluster) {
    return title;
  }

  const name = `${cluster.brand.name} ${cluster.name}`;
  return variant ? `${name} · ${variant}` : name;
};

const AnswerSummary = ({
  side,
  content,
}: {
  side: AnswerSide;
  content: AnswerCardContent;
}) => {
  const { borderClassName } = ANSWER_SIDES[side];

  return (
    <div
      className={`flex flex-col gap-1.5 rounded-[16px] border-2 bg-white px-4 py-3 ${borderClassName}`}
    >
      <AnswerChip side={side} />
      <span className="text-[14px] font-bold text-[#1e293b]">
        {summarizeAnswerCardContent(content)}
      </span>
    </div>
  );
};

type DisputeSendState =
  | { status: "editing" }
  | { status: "sending" }
  | { status: "failed"; errorMessage: string };

export const DisputeForm = ({
  labelerCardContent,
  correctCardContent,
  note,
  onNoteChange,
  isLocked,
  onCancel,
  onNextTrack,
  onSendDispute,
}: {
  labelerCardContent: AnswerCardContent;
  correctCardContent: AnswerCardContent;
  note: string;
  onNoteChange: (note: string) => void;
  isLocked: boolean;
  onCancel: () => void;
  onNextTrack: () => void;
  onSendDispute: (note: string) => Promise<void>;
}) => {
  const [sendState, setSendState] = useState<DisputeSendState>({
    status: "editing",
  });

  const isSending = sendState.status === "sending";
  const noteRef = useRef<HTMLTextAreaElement>(null);

  // The note box is disabled while sending, which drops focus out of the dialog, refocus.
  useEffect(() => {
    if (sendState.status === "failed") {
      noteRef.current?.focus();
    }
  }, [sendState.status]);

  const handleSend = async () => {
    setSendState({ status: "sending" });
    try {
      await onSendDispute(note.trim());
      setSendState({ status: "editing" });
    } catch (error) {
      setSendState({
        status: "failed",
        errorMessage:
          error instanceof Error ? error.message : "Please try again.",
      });
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-4">
        <AnswerSummary side="labeler" content={labelerCardContent} />
        <AnswerSummary side="correct" content={correctCardContent} />
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[16px] font-bold text-[#1e293b]">
          Think the correct answer is wrong?
        </span>
        <span className="text-[14px] text-[#64748b]">
          Staff will review this track. A note helps them see what you saw.
        </span>
      </div>

      <Textarea
        aria-label="Note for staff"
        placeholder="What do you see? (optional) For example: the packaging says 24 Cans"
        ref={noteRef}
        value={note}
        onChange={(event) => onNoteChange(event.currentTarget.value)}
        disabled={isSending}
        autosize
        minRows={5}
        radius="md"
        // The labeler chose to dispute, so the note is what they'll do next.
        autoFocus
      />

      {sendState.status === "failed" && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-[12px] bg-[#FEF2F2] px-3 py-2 text-[13px] text-[#B91C1C]"
        >
          <IconAlertTriangle size={16} className="shrink-0" />
          Couldn't send your dispute. {sendState.errorMessage}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        {sendState.status === "failed" ? (
          <button
            onClick={onNextTrack}
            disabled={isLocked}
            className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-[12px] font-bold uppercase tracking-[0.1em] text-[#64748b] hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Continue without disputing
            {isLocked && !isSending && <Loader size={14} color="gray" />}
          </button>
        ) : (
          <button
            onClick={onCancel}
            disabled={isSending || isLocked}
            className="rounded-xl px-4 py-2.5 text-[12px] font-bold uppercase tracking-[0.1em] text-[#64748b] hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSend}
          disabled={isSending || isLocked}
          className="flex items-center gap-2 rounded-xl bg-[#1e293b] px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.1em] text-white hover:bg-[#0f172a] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isSending ? (
            <Loader size={14} color="white" />
          ) : (
            <IconSend size={15} />
          )}
          {sendState.status === "failed" ? "Try again" : "Send dispute"}
        </button>
      </div>
    </div>
  );
};
